import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rewardApi } from '../../api/reward.api';
import { ProfitabilityResponse, EventRoiItem, DailyProfitItem } from '../../types/Reward.api.type';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { RevenueMixDonut } from './RevenueMixDonut';

const PERIOD_OPTIONS = [
  { value: 'YESTERDAY', label: '어제' },
  { value: 'TODAY', label: '오늘' },
  { value: '7D', label: '7일' },
  { value: '30D', label: '30일' },
  { value: 'ALL', label: '전체' },
];

export const ProfitabilityDashboard = () => {
  const [period, setPeriod] = useState('TODAY');

  const { data, isLoading } = useQuery({
    queryKey: ['adminProfitability', period],
    queryFn: () => rewardApi.getProfitability(period).then((res) => res.data.results as ProfitabilityResponse),
  });

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">로딩 중...</div>;
  }

  return (
    <div className="space-y-6">
      {/* 토스 FE 미배포 안내 배너 — S8-32 토스 검수 통과 후 본 div 제거 */}
      <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 text-sm text-yellow-900">
        ⚠️ <strong>R/F 분리 데이터 임시 안내</strong>
        <br />
        현재 토스 FE 배포 전 단계로 <strong>모든 광고 시청은 리워드로 집계</strong> 중입니다. 도넛 / 이벤트 테이블 /
        일별 차트의 <strong>전면 수익 0원 표시는 정상</strong>이며, 실제 R/F 분리는{' '}
        <strong>토스 검수 통과 후 자동으로 노출</strong>됩니다.
      </div>

      {/* 기간 필터 */}
      <div className="flex justify-end">
        <div className="flex gap-1">
          {PERIOD_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              variant={period === opt.value ? 'default' : 'outline'}
              size="sm"
              className="text-xs h-7 px-2"
              onClick={() => setPeriod(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      {/* 요약 카드 */}
      {data && <SummaryCards data={data} />}

      {/* 수익 구성 도넛 (신규) */}
      {data && (
        <RevenueMixDonut
          mix={data.revenueMix}
          periodLabel={PERIOD_OPTIONS.find((o) => o.value === period)?.label ?? ''}
          hasApproximated={data.hasApproximated}
          approximatedRatio={data.approximatedRatio}
        />
      )}

      {/* 이벤트별 ROI */}
      {data && data.eventRoi.length > 0 && <EventRoiTable items={data.eventRoi} />}

      {/* 일별 추이 차트 */}
      {data && data.dailyTrend.length > 0 && <DailyTrendChart items={data.dailyTrend} />}
    </div>
  );
};

// ==================== 요약 카드 ====================

const SummaryCards = ({ data }: { data: ProfitabilityResponse }) => {
  const isProfit = data.netProfit >= 0;
  const hasMix = data.rewardedMixRatio !== null && data.rewardedMixRatio !== undefined;
  const hasApprox = !!data.hasApproximated && (data.approximatedRatio ?? 0) > 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="p-5">
            <p className="text-xs text-gray-500 mb-1">총 지급 조각 (비용)</p>
            <p className="text-xl font-bold text-gray-900">{data.totalPieceCost.toLocaleString()}원</p>
          </div>
        </Card>
        <Card>
          <div className="p-5">
            <p className="text-xs text-gray-500 mb-1">추정 수익</p>
            <p className="text-xl font-bold text-blue-600">{data.estimatedRevenue.toLocaleString()}원</p>
          </div>
        </Card>
        <Card>
          <div className="p-5">
            <p className="text-xs text-gray-500 mb-1">순손익</p>
            <p className={`text-xl font-bold flex items-center gap-1 ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
              {isProfit ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              {data.netProfit.toLocaleString()}원
            </p>
          </div>
        </Card>
        <Card>
          <div className="p-5">
            <p className="text-xs text-gray-500 mb-1">현재 eCPM</p>
            <p className="text-sm font-medium text-gray-900">리워드 {data.currentRewardEcpm.toLocaleString()}원</p>
            <p className="text-sm font-medium text-gray-900">
              전면 {(data.currentInterstitialEcpm ?? 0).toLocaleString()}원
            </p>
          </div>
        </Card>
      </div>
      {(hasMix || hasApprox) && (
        <div className="flex gap-2 flex-wrap">
          {hasMix && (
            <Badge variant="secondary" className="text-xs">
              리워드 {data.rewardedMixRatio}% / 전면 {data.interstitialMixRatio}%
            </Badge>
          )}
          {hasApprox && (
            <span
              title={
                (data.approximatedRatio ?? 0) > 30 ? 'rewardedAdRatio 변경 이력이 있으면 신뢰도 추가 저하' : undefined
              }
            >
              <Badge
                variant="outline"
                className={`text-xs ${(data.approximatedRatio ?? 0) > 30 ? 'bg-yellow-50 border-yellow-300 text-yellow-800' : ''}`}
              >
                근사 {data.approximatedRatio}%
              </Badge>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== 이벤트별 ROI 테이블 ====================

const formatRevenue = (value: number | null | undefined): { text: string; isNull: boolean } => {
  if (value === null || value === undefined) return { text: '—', isNull: true };
  return { text: `${value.toLocaleString()}원`, isNull: false };
};

const AD_LINKED_TYPES = new Set([
  'VOTE_AD_BONUS',
  'DAILY_AD_BONUS',
  'MILESTONE_AD_BONUS',
  'ATTENDANCE_AD_BONUS',
  'ATTENDANCE_STREAK_SAVE',
  'COMMENT_AD_BONUS',
  'SURVEY_LIKE_AD_BONUS',
  'COMMENT_LIKE_AD_BONUS',
  'WEEKLY_MISSION_AD_BONUS',
  'ANALYSIS_UNLOCK',
  'CREATOR_MILESTONE_AD_BONUS',
  'ANALYSIS_FREE_VIEW',
  'EXCHANGE',
]);

const REWARD_TYPE_GROUPS = [
  {
    key: 'vote',
    label: '투표 계열',
    types: [
      'VOTE',
      'VOTE_AD_BONUS',
      'DAILY_BONUS',
      'DAILY_AD_BONUS',
      'WEEKLY_MILESTONE_1',
      'WEEKLY_MILESTONE_2',
      'WEEKLY_MILESTONE_3',
      'WEEKLY_MILESTONE_4',
      'MILESTONE_AD_BONUS',
      'FIRST_VOTE_BONUS',
      'CREATOR_MILESTONE',
    ],
  },
  {
    key: 'attendance',
    label: '출석 계열',
    types: ['ATTENDANCE', 'ATTENDANCE_AD_BONUS', 'ATTENDANCE_STREAK', 'ATTENDANCE_STREAK_SAVE'],
  },
  {
    key: 'activity',
    label: '활동 보상',
    types: [
      'COMMENT',
      'COMMENT_AD_BONUS',
      'SURVEY_LIKE',
      'SURVEY_LIKE_AD_BONUS',
      'COMMENT_LIKE',
      'COMMENT_LIKE_AD_BONUS',
      'WEEKLY_MISSION',
      'WEEKLY_MISSION_AD_BONUS',
    ],
  },
  { key: 'etc', label: '기타', types: [] as string[] },
];

const groupRewardItems = (items: EventRoiItem[]) => {
  const assignedTypes = new Set(REWARD_TYPE_GROUPS.flatMap((g) => g.types));
  return REWARD_TYPE_GROUPS.map((group) => {
    const groupItems =
      group.key === 'etc'
        ? items.filter((item) => !assignedTypes.has(item.rewardType))
        : items.filter((item) => group.types.includes(item.rewardType));
    const subtotal = groupItems.reduce(
      (acc, item) => ({
        issueCount: acc.issueCount + item.issueCount,
        issuePiece: acc.issuePiece + item.issuePiece,
        rewardConversionCount: acc.rewardConversionCount + (item.rewardConversionCount ?? 0),
        interstitialConversionCount: acc.interstitialConversionCount + (item.interstitialConversionCount ?? 0),
        rewardRevenue: acc.rewardRevenue + (item.rewardRevenue ?? 0),
        interstitialRevenue:
          item.interstitialRevenue === null || acc.interstitialRevenue === null
            ? null
            : (acc.interstitialRevenue ?? 0) + (item.interstitialRevenue ?? 0),
        totalRevenue:
          item.totalRevenue === null || acc.totalRevenue === null
            ? null
            : (acc.totalRevenue ?? 0) + (item.totalRevenue ?? item.estimatedRevenue),
        netProfit: acc.netProfit + item.netProfit,
      }),
      {
        issueCount: 0,
        issuePiece: 0,
        rewardConversionCount: 0,
        interstitialConversionCount: 0,
        rewardRevenue: 0,
        interstitialRevenue: 0 as number | null,
        totalRevenue: 0 as number | null,
        netProfit: 0,
      },
    );
    return { ...group, items: groupItems, subtotal };
  }).filter((g) => g.items.length > 0);
};

const EventRoiTable = ({ items }: { items: EventRoiItem[] }) => {
  const totals = items.reduce(
    (acc, item) => ({
      issueCount: acc.issueCount + item.issueCount,
      issuePiece: acc.issuePiece + item.issuePiece,
      rewardConversionCount: acc.rewardConversionCount + (item.rewardConversionCount ?? 0),
      interstitialConversionCount: acc.interstitialConversionCount + (item.interstitialConversionCount ?? 0),
      rewardRevenue: acc.rewardRevenue + (item.rewardRevenue ?? 0),
      interstitialRevenue:
        item.interstitialRevenue === null || acc.interstitialRevenue === null
          ? null
          : (acc.interstitialRevenue ?? 0) + (item.interstitialRevenue ?? 0),
      totalRevenue:
        item.totalRevenue === null || acc.totalRevenue === null
          ? null
          : (acc.totalRevenue ?? 0) + (item.totalRevenue ?? item.estimatedRevenue),
      netProfit: acc.netProfit + item.netProfit,
    }),
    {
      issueCount: 0,
      issuePiece: 0,
      rewardConversionCount: 0,
      interstitialConversionCount: 0,
      rewardRevenue: 0,
      interstitialRevenue: 0 as number | null,
      totalRevenue: 0 as number | null,
      netProfit: 0,
    },
  );

  const groups = groupRewardItems(items);

  return (
    <Card>
      <div className="p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">이벤트별 ROI</h3>

        {/* 데스크톱 테이블 */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="pb-2 pr-3">이벤트</th>
                <th className="pb-2 pr-3 text-right">지급 건수</th>
                <th className="pb-2 pr-3 text-right">지급 조각</th>
                <th className="pb-2 pr-3 text-right">리워드 전환</th>
                <th className="pb-2 pr-3 text-right">전면 전환</th>
                <th className="pb-2 pr-3 text-right">전환율</th>
                <th className="pb-2 pr-3 text-right">리워드 수익</th>
                <th className="pb-2 pr-3 text-right">전면 수익</th>
                <th className="pb-2 pr-3 text-right">합산 수익</th>
                <th className="pb-2 text-right">순손익</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group, gi) => (
                <React.Fragment key={group.key}>
                  {/* 그룹 헤더 */}
                  <tr className={`bg-gray-100 ${gi > 0 ? 'border-t-2' : ''}`}>
                    <td colSpan={10} className="py-1.5 px-3 text-xs font-semibold text-gray-600 uppercase">
                      {group.label}
                    </td>
                  </tr>
                  {/* 그룹 아이템 */}
                  {group.items.map((item) => {
                    const approx = !!item.isApproximated;
                    return (
                      <tr
                        key={item.rewardType}
                        className={`border-b hover:bg-gray-50 ${approx ? 'bg-gray-50' : ''}`}
                        title={approx ? 'S8-26 이전 데이터는 rewardedAdRatio로 근사 분배' : undefined}
                      >
                        <td className="py-2 pr-3 pl-3">
                          <span className="font-medium">{item.label}</span>
                          {AD_LINKED_TYPES.has(item.rewardType) && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              광고
                            </Badge>
                          )}
                          {approx && <span className="ml-1 text-gray-400 text-xs">(근사)</span>}
                        </td>
                        <td className="py-2 pr-3 text-right font-mono">{item.issueCount.toLocaleString()}</td>
                        <td className="py-2 pr-3 text-right font-mono">{item.issuePiece.toLocaleString()}</td>
                        <td className="py-2 pr-3 text-right font-mono">
                          {(item.rewardConversionCount ?? 0).toLocaleString()}
                        </td>
                        <td className="py-2 pr-3 text-right font-mono">
                          {(item.interstitialConversionCount ?? 0).toLocaleString()}
                        </td>
                        <td className="py-2 pr-3 text-right font-mono">{item.conversionRate}%</td>
                        <td className="py-2 pr-3 text-right font-mono">
                          {(item.rewardRevenue ?? 0).toLocaleString()}원
                        </td>
                        {(() => {
                          const f = formatRevenue(item.interstitialRevenue);
                          return (
                            <td
                              className={`py-2 pr-3 text-right font-mono ${f.isNull ? 'text-gray-400' : ''}`}
                              title={f.isNull ? '전면 eCPM 미입력' : ''}
                            >
                              {f.text}
                            </td>
                          );
                        })()}
                        {(() => {
                          const f = formatRevenue(item.totalRevenue);
                          return (
                            <td
                              className={`py-2 pr-3 text-right font-mono ${f.isNull ? 'text-gray-400' : ''}`}
                              title={f.isNull ? '전면 eCPM 미입력으로 합산 불가' : ''}
                            >
                              {f.text}
                            </td>
                          );
                        })()}
                        <td
                          className={`py-2 text-right font-mono font-medium ${item.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}
                        >
                          {item.netProfit.toLocaleString()}원
                        </td>
                      </tr>
                    );
                  })}
                  {/* 그룹 소계 */}
                  <tr className="bg-gray-50/70">
                    <td className="py-1.5 pr-3 pl-3 text-sm font-medium text-gray-600">{group.label} 소계</td>
                    <td className="py-1.5 pr-3 text-right font-mono text-sm text-gray-600">
                      {group.subtotal.issueCount.toLocaleString()}
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-sm text-gray-600">
                      {group.subtotal.issuePiece.toLocaleString()}
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-sm text-gray-600">
                      {group.subtotal.rewardConversionCount.toLocaleString()}
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-sm text-gray-600">
                      {group.subtotal.interstitialConversionCount.toLocaleString()}
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-sm text-gray-400">-</td>
                    <td className="py-1.5 pr-3 text-right font-mono text-sm text-gray-600">
                      {group.subtotal.rewardRevenue.toLocaleString()}원
                    </td>
                    {(() => {
                      const f = formatRevenue(group.subtotal.interstitialRevenue);
                      return (
                        <td
                          className={`py-1.5 pr-3 text-right font-mono text-sm ${f.isNull ? 'text-gray-400' : 'text-gray-600'}`}
                          title={f.isNull ? '전면 eCPM 미입력' : ''}
                        >
                          {f.text}
                        </td>
                      );
                    })()}
                    {(() => {
                      const f = formatRevenue(group.subtotal.totalRevenue);
                      return (
                        <td
                          className={`py-1.5 pr-3 text-right font-mono text-sm ${f.isNull ? 'text-gray-400' : 'text-gray-600'}`}
                          title={f.isNull ? '전면 eCPM 미입력으로 합산 불가' : ''}
                        >
                          {f.text}
                        </td>
                      );
                    })()}
                    <td
                      className={`py-1.5 text-right font-mono text-sm font-medium ${group.subtotal.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}
                    >
                      {group.subtotal.netProfit.toLocaleString()}원
                    </td>
                  </tr>
                </React.Fragment>
              ))}
              {/* 총합 행 */}
              <tr className="bg-gray-50 font-medium border-t-2">
                <td className="py-2 pr-3">합계</td>
                <td className="py-2 pr-3 text-right font-mono">{totals.issueCount.toLocaleString()}</td>
                <td className="py-2 pr-3 text-right font-mono">{totals.issuePiece.toLocaleString()}</td>
                <td className="py-2 pr-3 text-right font-mono">{totals.rewardConversionCount.toLocaleString()}</td>
                <td className="py-2 pr-3 text-right font-mono">
                  {totals.interstitialConversionCount.toLocaleString()}
                </td>
                <td className="py-2 pr-3 text-right font-mono text-gray-400">-</td>
                <td className="py-2 pr-3 text-right font-mono">{totals.rewardRevenue.toLocaleString()}원</td>
                {(() => {
                  const f = formatRevenue(totals.interstitialRevenue);
                  return (
                    <td
                      className={`py-2 pr-3 text-right font-mono ${f.isNull ? 'text-gray-400' : ''}`}
                      title={f.isNull ? '전면 eCPM 미입력' : ''}
                    >
                      {f.text}
                    </td>
                  );
                })()}
                {(() => {
                  const f = formatRevenue(totals.totalRevenue);
                  return (
                    <td
                      className={`py-2 pr-3 text-right font-mono ${f.isNull ? 'text-gray-400' : ''}`}
                      title={f.isNull ? '전면 eCPM 미입력으로 합산 불가' : ''}
                    >
                      {f.text}
                    </td>
                  );
                })()}
                <td
                  className={`py-2 text-right font-mono font-medium ${totals.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}
                >
                  {totals.netProfit.toLocaleString()}원
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 모바일 카드 */}
        <div className="md:hidden space-y-3">
          {groups.map((group, gi) => (
            <React.Fragment key={group.key}>
              {/* 그룹 헤더 */}
              <div
                className={`text-xs font-semibold text-gray-600 uppercase bg-gray-100 rounded px-3 py-1.5 ${gi > 0 ? 'mt-4' : ''}`}
              >
                {group.label}
              </div>
              {/* 그룹 아이템 */}
              {group.items.map((item) => (
                <div
                  key={item.rewardType}
                  className={`border rounded-lg p-3 space-y-2 ${item.isApproximated ? 'border-gray-300 bg-gray-50' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{item.label}</span>
                    {AD_LINKED_TYPES.has(item.rewardType) && (
                      <Badge variant="outline" className="text-xs">
                        광고
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">지급:</span> {item.issueCount.toLocaleString()}건 /{' '}
                      {item.issuePiece.toLocaleString()}조각
                    </div>
                    <div>
                      <span className="text-gray-500">리워드전환:</span>{' '}
                      {(item.rewardConversionCount ?? 0).toLocaleString()}건
                    </div>
                    <div>
                      <span className="text-gray-500">전면전환:</span>{' '}
                      {(item.interstitialConversionCount ?? 0).toLocaleString()}건
                    </div>
                    <div>
                      <span className="text-gray-500">리워드수익:</span> {(item.rewardRevenue ?? 0).toLocaleString()}원
                    </div>
                    {(() => {
                      const f = formatRevenue(item.interstitialRevenue);
                      return (
                        <div title={f.isNull ? '전면 eCPM 미입력' : ''}>
                          <span className="text-gray-500">전면수익:</span>{' '}
                          <span className={f.isNull ? 'text-gray-400' : ''}>{f.text}</span>
                        </div>
                      );
                    })()}
                    <div className={item.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                      <span className="text-gray-500">순손익:</span> {item.netProfit.toLocaleString()}원
                    </div>
                  </div>
                </div>
              ))}
              {/* 그룹 소계 */}
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50/70">
                <div className="font-medium text-sm text-gray-600 mb-2">{group.label} 소계</div>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div>
                    <span className="text-gray-500">지급:</span> {group.subtotal.issueCount.toLocaleString()}건 /{' '}
                    {group.subtotal.issuePiece.toLocaleString()}조각
                  </div>
                  <div>
                    <span className="text-gray-500">리워드전환:</span>{' '}
                    {group.subtotal.rewardConversionCount.toLocaleString()}건
                  </div>
                  <div>
                    <span className="text-gray-500">전면전환:</span>{' '}
                    {group.subtotal.interstitialConversionCount.toLocaleString()}건
                  </div>
                  {(() => {
                    const f = formatRevenue(group.subtotal.totalRevenue);
                    return (
                      <div title={f.isNull ? '전면 eCPM 미입력으로 합산 불가' : ''}>
                        <span className="text-gray-500">합산수익:</span>{' '}
                        <span className={f.isNull ? 'text-gray-400' : ''}>{f.text}</span>
                      </div>
                    );
                  })()}
                  <div
                    className={
                      group.subtotal.netProfit >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'
                    }
                  >
                    <span className="text-gray-500">순손익:</span> {group.subtotal.netProfit.toLocaleString()}원
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))}
          {/* 모바일 총합 */}
          <div className="border-2 border-gray-300 rounded-lg p-3 bg-gray-50 mt-4">
            <div className="font-medium text-sm mb-2">합계</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-500">지급:</span> {totals.issueCount.toLocaleString()}건 /{' '}
                {totals.issuePiece.toLocaleString()}조각
              </div>
              <div>
                <span className="text-gray-500">리워드전환:</span> {totals.rewardConversionCount.toLocaleString()}건
              </div>
              <div>
                <span className="text-gray-500">전면전환:</span> {totals.interstitialConversionCount.toLocaleString()}건
              </div>
              {(() => {
                const f = formatRevenue(totals.totalRevenue);
                return (
                  <div title={f.isNull ? '전면 eCPM 미입력으로 합산 불가' : ''}>
                    <span className="text-gray-500">합산수익:</span>{' '}
                    <span className={f.isNull ? 'text-gray-400' : ''}>{f.text}</span>
                  </div>
                );
              })()}
              <div className={totals.netProfit >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                <span className="text-gray-500">순손익:</span> {totals.netProfit.toLocaleString()}원
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

// ==================== 일별 추이 차트 ====================

const DailyTrendChart = ({ items }: { items: DailyProfitItem[] }) => (
  <Card>
    <div className="p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">일별 추이</h3>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={items}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v) => `${Number(v ?? 0).toLocaleString()}원`} />
          <Legend />
          <Line
            type="monotone"
            dataKey="rewardRevenue"
            name="리워드 수익"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="interstitialRevenue"
            name="전면 수익"
            stroke="#a855f7"
            strokeWidth={2}
            strokeDasharray="4 4"
            dot={false}
          />
          <Line type="monotone" dataKey="pieceCost" name="조각 비용" stroke="#ef4444" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="netProfit" name="순손익" stroke="#22c55e" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>

      {/* 일별 상세 테이블 */}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 pr-2">날짜</th>
              <th className="pb-2 pr-2 text-right">조각 비용</th>
              <th className="pb-2 pr-2 text-right">리워드 시청</th>
              <th className="pb-2 pr-2 text-right">추정 수익</th>
              <th className="pb-2 text-right">순손익</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.date} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-1.5 pr-2">{item.date}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{item.pieceCost.toLocaleString()}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{item.rewardAdWatch.toLocaleString()}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{item.estimatedRevenue.toLocaleString()}</td>
                <td
                  className={`py-1.5 text-right font-mono font-medium ${item.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}
                >
                  {item.netProfit.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </Card>
);
